import Vue from 'vue';
import Vuex from 'vuex';
Vue.use(Vuex);

import badges from './badges.js';
import spaceships from './spaceships.js';
import gifts from './gifts.js';
import planets from './planets.js';
import surprises from './surprises.js';
import achievements from './achievements.js';

const store = new Vuex.Store({
    state: {
        game: {},
        badges,
        spaceships,
        gifts,
        planets,
        surprises,
        achievements,
        HTMLElementsVisible: true,
        prevRoute: ''
    },

    getters: {
        isGameDataAvailable: state => Object.keys(state.game).length > 0,
        HTMLElementsVisible: state => state.HTMLElementsVisible,
        nickname: state => (state.game && state.game.user && state.game.user.nickname) ? state.game.user.nickname : '',
        avatar: state => (state.game && state.game.user && state.game.user.avatar) ? state.game.user.avatar : '',
        isPupil: state => state.game && state.game.start !== null,
        startDate: state => state.game && state.game.start ? state.game.start : '',
        isGamePaused: state => !!(state.game && state.game.user && state.game.user.pause),
        playThrough: state => !!(state.game && state.game.user && state.game.user.play_through),
        currentRoute: state => (state.game && state.game.user && state.game.user.current_route) ? state.game.user.current_route : 'w1d1exploration1',
        currentWeek: state => {
            if (state.game && state.game.user && state.game.user.week1 === null) {
                return 1;
            } else if (state.game && state.game.user && state.game.user.week2 === null) {
                return 2;
            } else if (state.game && state.game.user && state.game.user.week3 === null) {
                return 3;
            } else if (state.game && state.game.user && state.game.user.week4 === null) {
                return 4;
            }

            return 1;
        },
        getWeekRoute: state => week => {
            if (state.game && state.game.user && state.game.user[`week${week}_route`]) {
                return state.game.user[`week${week}_route`];
            }

            return null;
        },

        // stars
        stars: state => (state.game && state.game.user && state.game.user.stars) ? state.game.user.stars.toString() : '0',
        spendStars: state => (state.game && state.game.user && state.game.user.spend_stars) ? state.game.user.spend_stars.toString() : '0',
        totalStars: state => (state.game && state.game.user && state.game.user.total_stars) ? state.game.user.total_stars.toString() : '0',

        // badges
        badges: state => state.badges,
        getBadge: state => name => {
            return state.badges.find(badge => badge.name === name);
        },
        hasUserBadge: state => name => {
            return state.game && state.game.badges && state.game.badges.some(badge => badge.name === name);
        },
        deservesUserBadge: state => (week, day, game, minValue = 1) => {
            let result = state.game.results.find(result => result.week === week && result.day === day && result.game === game);
            if (result) {
                return result.value >= minValue;
            }

            return false;
        },

        // spaceships
        spaceships: state => state.spaceships,
        hasUserSpaceship: state => name => {
            return state.game && state.game.spaceships && state.game.spaceships.some(spaceship => spaceship.name === name);
        },
        getUserSpaceship: state => (state.game && state.game.user && state.game.user.current_spaceship) ? state.game.user.current_spaceship : 'spaceship_1',

        // gifts
        gifts: state => state.gifts,
        getGift: state => name => {
            return state.gifts.find(gift => gift.name === name);
        },
        getUserGiftByWeek: state => (week) => {
            return state.game.gifts.find(gift => gift.week === week);
        },

        // planets
        planets: state => state.planets,
        getPlanetByWeek: state => week => {
            return state.planets.find(planet => planet.week === week);
        },

        // results
        hasUserResult: state => (week, day, game) => {
            return state.game && state.game.results && state.game.results.some(result => result.week === week && result.day === day && result.game === game);
        },

        // finished days/week/game
        hasUserFinishedDay: state => (week, day) => {
            return state.game && state.game.finished_days && state.game.finished_days.some(finishedDay => finishedDay.week === week && finishedDay.day === day);
        },
        hasUserFinishedWeek: state => (week) => {
            return state.game.user[`week${week}`] !== null;
        },
        hasUserFinishedGame: state => {
            return state.game.user.week1 !== null &&
                state.game.user.week2 !== null &&
                state.game.user.week3 !== null &&
                state.game.user.week4 !== null;
        },

        // surprises
        surprises: state => state.surprises,
        hasUserSurprise: state => (week, day) => {
            return state.game && state.game.surprises && state.game.surprises.some(surprise => surprise.week === week && surprise.day === day);
        },
        hasUserUnopenedSurprise: (state, getters) => {
            return state.surprises.some(surprise => getters.isDayUnlocked(surprise.week, surprise.day) && !getters.hasUserSurprise(surprise.week, surprise.day));
        },

        // game journey
        isDayUnlocked: (state, getters) => (week, day) => {
            if (!getters.isPupil || getters.playThrough) {
                return true;
            }

            let now = moment();
            let startDate = moment(getters.startDate, 'YYYY-MM-DD')
                .add(week - 1, 'week')
                .add(day - 1, 'day');

            return startDate < now;
        },
        isRouteUnlocked: (state, getters) => (route) => {
            if (route && route.meta) {
                return getters.isDayUnlocked(route.meta.week, route.meta.day);
            }

            return false;
        },
        routeTimeDifference: (state, getters) => (route) => {
            if (route && route.meta) {
                let now = moment();
                let startDate = moment(getters.startDate, 'YYYY-MM-DD')
                    .add(route.meta.week - 1, 'week')
                    .add(route.meta.day - 1, 'day');

                let duration = moment.duration(startDate.diff(now));

                return `${parseInt(duration.asHours())} STD ${parseInt(duration.asMinutes() % 60)} MIN`;
            }

            return '';
        },

        // achievements
        achievements: state => state.achievements,

        // previous route
        prevRoute: state => state.prevRoute
    },

    mutations: {
        storeGameData (state, data) {
            state.game = data;
        },
        storeCurrentRoute (state, route) {
            state.game.user.current_route = route;
        },
        showHTMLElements (state) {
            state.HTMLElementsVisible = true;
        },
        hideHTMLElements (state) {
            state.HTMLElementsVisible = false;
        },
        storePrevRoute (state, route) {
            state.prevRoute = route;
        }
    }
});

export default store;
